import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Activity, Box, RadioTower } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPercent, formatRequestRate } from './graph-utils';
import { nodePresentation } from './node-presentation';
import type { ArchitectureFlowNode } from './types';

const healthClasses = {
  healthy: 'bg-green-500',
  degraded: 'bg-amber-500',
  unhealthy: 'bg-red-500',
  unknown: 'bg-neutral-400',
};

export function ArchitectureGraphNode({ data, selected }: NodeProps<ArchitectureFlowNode>) {
  const { topologyNode, detailLevel, hasEventTraffic } = data;
  const presentation = nodePresentation(topologyNode);
  const Icon =
    topologyNode.kind === 'topic' ? RadioTower : topologyNode.kind === 'external' ? Box : Activity;
  return (
    <div
      className={cn(
        'min-w-52 rounded-xl border p-3 shadow-card transition-shadow',
        presentation.className,
        selected && 'ring-2 ring-lavender-500 ring-offset-2'
      )}
      aria-label={`${topologyNode.label}, ${topologyNode.health}`}
    >
      <Handle type="target" position={Position.Left} className="!border-0 !bg-lavender-500" />
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-neutral-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-neutral-900">{topologyNode.label}</p>
          <p className="mt-0.5 text-xs capitalize text-neutral-500">{presentation.label}</p>
          {topologyNode.kind === 'service' && hasEventTraffic && (
            <span className="mt-1 inline-flex rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800">
              Events
            </span>
          )}
        </div>
        <span
          className={cn(
            'mt-1 h-2.5 w-2.5 shrink-0 rounded-full',
            healthClasses[topologyNode.health]
          )}
          aria-label={topologyNode.health}
        />
      </div>
      {detailLevel !== 'overview' && (
        <p className="mt-3 text-xs text-neutral-600">
          {topologyNode.evidence === 'configured'
            ? 'No observed traffic'
            : formatRequestRate(topologyNode.requestRate)}
        </p>
      )}
      {detailLevel === 'detailed' && topologyNode.evidence !== 'configured' && (
        <p className="mt-1 text-xs text-neutral-500">
          {formatPercent(topologyNode.errorRate)} errors · p95{' '}
          {topologyNode.p95LatencyMs.toFixed(0)} ms
        </p>
      )}
      <Handle type="source" position={Position.Right} className="!border-0 !bg-lavender-500" />
    </div>
  );
}
